import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import MiniCalendar from "components/calendar/MiniCalendar";
import TotalSpent from "views/seo/dashboard/components/TotalSpent";
import TotalLinksOverall from "views/seo/dashboard/components/TotalLinksOverall";
import { IoDocuments } from "react-icons/io5";
import { MdBarChart, MdDashboard } from "react-icons/md";
import Widget from "components/widget/Widget";
import Banner from "./components/Banner";
import OverdueTasks from "./components/OverdueTasks";

const Dashboard = () => {
  const { fetchWithAuth, currentLang, API_URL, getCsrfToken, user } = useAuth();
  
  const [stats, setStats] = useState({
    sites: 0,
    pages: 0,
    links: 0,
    currentMonthTasks: { completed: 0, total: 0 },
    lastMonthTasks: { completed: 0, total: 0 }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загрузка статистики
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Загружаем сайты
      const sitesResponse = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const sitesData = await sitesResponse.json();
      let sitesCount = 0;
      let pagesCount = 0;

      if (sitesResponse.ok && sitesData.success) {
        sitesCount = sitesData.sites?.length || 0;
        // Подсчитываем общее количество страниц
        pagesCount = sitesData.sites?.reduce((total, site) => {
          return total + (site.pages_count || 0);
        }, 0) || 0;
      }

      // Загружаем ссылки (только личные)
      const linksResponse = await fetchWithAuth(`${API_URL}${currentLang}/links?user_id=${user?.id || ''}`, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const linksData = await linksResponse.json();
      let linksCount = 0;

      if (linksResponse.ok && linksData.success) {
        linksCount = linksData.pagination?.total || 0;
      }

      // Функции для работы с датами
      const getCurrentMonthRange = () => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        return {
          from: startOfMonth.toISOString().split('T')[0],
          to: endOfMonth.toISOString().split('T')[0]
        };
      };

      const getLastMonthRange = () => {
        const now = new Date();
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        
        return {
          from: startOfLastMonth.toISOString().split('T')[0],
          to: endOfLastMonth.toISOString().split('T')[0]
        };
      };

      // Загружаем задачи текущего месяца
      const currentMonth = getCurrentMonthRange();
      const currentMonthResponse = await fetchWithAuth(`${API_URL}${currentLang}/seo-tasks?d_start_from=${currentMonth.from}&d_start_to=${currentMonth.to}&limit=1000&uid=${user?.id || ''}`, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const currentMonthData = await currentMonthResponse.json();
      let currentMonthTasks = { completed: 0, total: 0 };

      if (currentMonthResponse.ok && currentMonthData.success) {
        const tasks = currentMonthData.data?.tasks || [];
        currentMonthTasks = {
          total: tasks.length,
          completed: tasks.filter(task => task.status === 2).length // status 2 = завершена
        };
      }

      // Загружаем задачи прошлого месяца
      const lastMonth = getLastMonthRange();
      const lastMonthResponse = await fetchWithAuth(`${API_URL}${currentLang}/seo-tasks?d_start_from=${lastMonth.from}&d_start_to=${lastMonth.to}&limit=1000&uid=${user?.id || ''}`, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const lastMonthData = await lastMonthResponse.json();
      let lastMonthTasks = { completed: 0, total: 0 };

      if (lastMonthResponse.ok && lastMonthData.success) {
        const tasks = lastMonthData.data?.tasks || [];
        lastMonthTasks = {
          total: tasks.length,
          completed: tasks.filter(task => task.status === 2).length // status 2 = завершена
        };
      }

      setStats({
        sites: sitesCount,
        pages: pagesCount,
        links: linksCount,
        currentMonthTasks,
        lastMonthTasks
      });

    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError('Ошибка загрузки данных');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Card widget */}
      <div className="mt-3 grid h-full grid-cols-1 gap-3 sm:gap-4 lg:gap-5">
        <div className="col-span-1 h-fit w-full xl:col-span-1 2xl:col-span-2">
          {/* Banner */}
          <Banner />
        </div>
      </div>
      
      <div className="mt-3 grid grid-cols-1 gap-3 sm:gap-4 lg:gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-5">
        <Widget
          icon={<MdBarChart className="h-6 w-6 sm:h-7 sm:w-7" />}
          title={"Сайтов"}
          subtitle={loading ? "..." : stats.sites.toString()}
        />
        <Widget
          icon={<IoDocuments className="h-5 w-5 sm:h-6 sm:w-6" />}
          title={"Страниц"}
          subtitle={loading ? "..." : stats.pages.toString()}
        />
        <Widget
          icon={<MdBarChart className="h-6 w-6 sm:h-7 sm:w-7" />}
          title={"Мои ссылки"}
          subtitle={loading ? "..." : stats.links.toString()}
        />
        <Widget
          icon={<MdDashboard className="h-5 w-5 sm:h-6 sm:w-6" />}
          title={"Текущий месяц"}
          subtitle={
            loading ? "..." : (
              <span className={`text-sm sm:text-base ${stats.currentMonthTasks.completed < stats.currentMonthTasks.total ? 'text-red-500' : 'text-green-500'}`}>
                {stats.currentMonthTasks.completed} / {stats.currentMonthTasks.total}
              </span>
            )
          }
        />
        <Widget
          icon={<MdDashboard className="h-5 w-5 sm:h-6 sm:w-6" />}
          title={"Прошлый месяц"}
          subtitle={
            loading ? "..." : (
              <span className={`text-sm sm:text-base ${stats.lastMonthTasks.completed < stats.lastMonthTasks.total ? 'text-red-500' : 'text-green-500'}`}>
                {stats.lastMonthTasks.completed} / {stats.lastMonthTasks.total}
              </span>
            )
          }
        />
      </div>

      {error && (
        <div className="mt-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm sm:text-base">
          {error}
        </div>
      )}

      {/* Просроченные задачи */}
      <div className="mt-4 sm:mt-5 grid grid-cols-1 gap-3 sm:gap-4 lg:gap-5">
        <OverdueTasks />
      </div>

      {/* Charts */}
      <div className="mt-4 sm:mt-5 grid grid-cols-1 gap-3 sm:gap-4 lg:gap-5">
        <TotalSpent />
      </div>

      {/* Charts */}
      <div className="mt-4 sm:mt-5 grid grid-cols-1 gap-3 sm:gap-4 lg:gap-5">
        <TotalLinksOverall />
      </div>
      
      {/* Tables & Charts */}
    </div>
  );
};

export default Dashboard;
